import { useState } from 'react';
import { Modal, ModalBody, Text, Stack } from '@7shifts/sous-chef-base';
import { PinEntryGrid } from '@src/components/PunchPad/PinEntryGrid';
import { useTranslation } from '@src/i18n';

interface VerifyPinModalProps {
    /**
     * Verifies the entered punch ID against the authenticated admin's. Resolves
     * `true` to run the gated action; `false` shows the mismatch error. The
     * comparison happens against a salted digest inside the account context —
     * the plaintext punch ID is never handed to this component (API-4551).
     * Fail-closed: resolves `false` when the account has no punch ID.
     */
    verify: (entered: string) => Promise<boolean>;
    title: string;
    submitLabel: string;
    onVerified: () => void;
    onClose: () => void;
}

export function VerifyPinModal({
    verify,
    title,
    submitLabel,
    onVerified,
    onClose
}: VerifyPinModalProps) {
    const { t } = useTranslation();
    const [showError, setShowError] = useState(false);
    const [checking, setChecking] = useState(false);

    async function handleSubmit(entered: string) {
        setChecking(true);
        let ok = false;
        try {
            ok = await verify(entered);
        } catch {
            // Fail closed: a rejected verification is treated as a mismatch
            // rather than propagating (which would leave `checking` stuck and
            // trap the user in the modal via handleClose).
            ok = false;
        } finally {
            setChecking(false);
        }
        if (ok) {
            onVerified();
        } else {
            setShowError(true);
        }
    }

    // Block dismissal while a verification is in flight: closing mid-check
    // would still let the pending handleSubmit resolve and fire onVerified,
    // executing the gated action after the modal was dismissed (API-4551).
    function handleClose() {
        if (checking) return;
        onClose();
    }

    return (
        <Modal onClose={handleClose} testId="verify-pin-modal">
            <ModalBody>
                <Stack space={20} alignItems="center">
                    <Text as="h1" alignment="center">
                        {title}
                    </Text>
                    <PinEntryGrid
                        onSubmit={pin => void handleSubmit(pin)}
                        disabled={checking}
                        loading={checking}
                        submitLabel={submitLabel}
                    />
                    {showError && (
                        <Text
                            as="paragraph"
                            alignment="center"
                            color="var(--color-danger-color)"
                        >
                            {t('settings.verifyError')}
                        </Text>
                    )}
                </Stack>
            </ModalBody>
        </Modal>
    );
}
